import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Loader2, Send, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { LinkifiedText } from '@/components/LinkifiedText';

const KINDS = { promo: 'Promocional', service: 'Essencial de serviço' };
const AUDIENCES = { all: 'Todos', free: 'Gratuitos', premium: 'Premium', individual: 'Individual' };
const newKey = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function Choice({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(options).map(([k, label]) => (
        <Button key={k} size="sm" variant={value === k ? 'default' : 'outline'} onClick={() => onChange(k)} className={value === k ? 'bg-ruby' : 'border-border'} data-testid={`${testid}-${k}`}>{label}</Button>
      ))}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-4 rounded-xl bg-surface border border-border" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{value ?? '—'}</p>
    </div>
  );
}

function UserPicker({ selected, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      api.get(`/admin/users/list?search=${encodeURIComponent(q)}&limit=8`).then(r => setResults(r.data.filter(u => !u.is_admin))).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  if (selected) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="im-selected-user">
        <span className="text-foreground">{selected.name} · {selected.email}</span>
        <Button size="sm" variant="ghost" onClick={() => onSelect(null)} data-testid="im-clear-user">Trocar</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Pesquisar por nome ou e-mail" className="pl-9 bg-background border-border" data-testid="im-user-search" />
      </div>
      {results.map(u => (
        <button key={u.id} onClick={() => onSelect(u)} className="w-full text-left text-sm p-2 rounded-md bg-white/5 hover:bg-white/10" data-testid={`im-user-option-${u.id}`}>
          {u.name} <span className="text-muted-foreground">· {u.email}</span>
        </button>
      ))}
    </div>
  );
}

export default function AdminInternalMessages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('promo');
  const [audience, setAudience] = useState('all');
  const [target, setTarget] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [sending, setSending] = useState(false);
  const [key, setKey] = useState(newKey);

  const loadOv = useCallback(() => api.get('/admin/im/overview').then(r => setOv(r.data)).catch(() => toast.error('Erro ao carregar')), []);
  useEffect(() => {
    if (!user?.is_admin) { navigate('/home'); return; }
    loadOv();
  }, [user, navigate, loadOv]);

  const canPrepare = body.trim() && (audience !== 'individual' || target);

  const prepare = async () => {
    try {
      const params = new URLSearchParams({ kind, audience, ...(target ? { user_id: target.id } : {}) });
      const { data } = await api.get(`/admin/im/audience-count?${params}`);
      setConfirm({ count: data.count });
    } catch {
      toast.error('Erro ao contar destinatários');
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/admin/im/send', { body, kind, audience, user_id: target?.id, idempotency_key: key, confirmed_count: confirm.count });
      toast.success(`Mensagem entregue a ${data.recipient_count} conta(s).`);
      setBody(''); setTarget(null); setConfirm(null); setKey(newKey());
      loadOv();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar');
      if (e?.response?.status === 409) setConfirm(null);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 pt-24 pb-16 space-y-6" data-testid="admin-im-page">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-ruby" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Mensagens</h1>
            <p className="text-sm text-muted-foreground">Mensagens internas da Intimate.pt para os leitores. Sem e-mail.</p>
          </div>
        </div>

        {ov && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat testid="im-stat-eligible" label="Contas elegíveis" value={ov.eligible} />
            <Stat testid="im-stat-accepted" label="Aceitaram" value={ov.accepted} />
            <Stat testid="im-stat-declined" label="Recusaram" value={ov.declined} />
            <Stat testid="im-stat-pending" label="Sem resposta" value={ov.pending} />
            <Stat testid="im-stat-rate" label="Adesão" value={ov.adhesion_rate == null ? 'Dados insuficientes' : `${ov.adhesion_rate}%`} />
          </div>
        )}
        {ov && (
          <p className="text-xs text-muted-foreground" data-testid="im-accounts-note">
            {ov.total_accounts} contas registadas · {ov.admins} administrador(es) e {ov.test_accounts} conta(s) de teste excluídas.
          </p>
        )}

        <Card className="bg-surface border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-base">Nova mensagem</CardTitle>
            <CardDescription>Texto simples, emojis e parágrafos. Os links começados por https:// ficam clicáveis.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea value={body} onChange={e => setBody(e.target.value)} rows={6} maxLength={3000} placeholder="Escreve a mensagem…" className="bg-background border-border" data-testid="im-body" />
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Tipo</p>
              <Choice options={KINDS} value={kind} onChange={setKind} testid="im-kind" />
              <p className="text-xs text-muted-foreground">
                {kind === 'promo' ? 'Só chega a quem aceitou mensagens internas.' : 'Apenas para informação necessária sobre a conta, subscrição ou serviço. Nunca para promoções.'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Destinatários</p>
              <Choice options={AUDIENCES} value={audience} onChange={(a) => { setAudience(a); setTarget(null); }} testid="im-audience" />
              {audience === 'individual' && <UserPicker selected={target} onSelect={setTarget} />}
            </div>
            {body.trim() && (
              <div className="rounded-xl border border-border p-4 bg-background" data-testid="im-preview">
                <p className="text-xs text-muted-foreground mb-2">Pré-visualização · Intimate.pt</p>
                <LinkifiedText text={body} className="text-sm text-foreground" />
              </div>
            )}
            <Button disabled={!canPrepare} onClick={prepare} className="bg-ruby hover:bg-ruby-light text-white" data-testid="im-prepare">
              <Send className="w-4 h-4 mr-2" />Rever e enviar
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-surface border-border">
          <CardHeader><CardTitle className="text-foreground text-base">Enviadas</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {ov?.history?.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Ainda não foram enviadas mensagens.</p>}
            {ov?.history?.map(m => (
              <div key={m.id} className="p-3 rounded-lg bg-white/5" data-testid={`im-history-${m.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>{new Date(m.sent_at).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })} · {KINDS[m.kind]} · {AUDIENCES[m.audience]}</span>
                  <span data-testid={`im-history-reads-${m.id}`}>{m.read_count}/{m.recipient_count} lidas ({m.read_rate}%)</span>
                </div>
                <p className="text-sm text-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </main>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && !sending && setConfirm(null)}>
        <DialogContent className="bg-surface border-border" data-testid="im-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Confirmar envio</DialogTitle>
            <DialogDescription>Revê antes de enviar. Esta ação não pode ser anulada.</DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="space-y-3 text-sm">
              <p><span className="text-muted-foreground">Tipo:</span> <span className="text-foreground" data-testid="im-confirm-kind">{KINDS[kind]}</span></p>
              <p><span className="text-muted-foreground">Destinatários:</span> <span className="text-foreground">{AUDIENCES[audience]}{target ? ` (${target.email})` : ''}</span></p>
              <p className="flex items-center gap-2"><Users className="w-4 h-4 text-ruby" /><span className="text-foreground font-semibold" data-testid="im-confirm-count">{confirm.count} conta(s) elegível(eis)</span></p>
              <div className="rounded-lg border border-border p-3 max-h-48 overflow-y-auto"><LinkifiedText text={body} className="text-foreground" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={sending} onClick={() => setConfirm(null)} className="border-border" data-testid="im-confirm-cancel">Cancelar</Button>
            <Button disabled={sending || !confirm?.count} onClick={send} className="bg-ruby hover:bg-ruby-light text-white" data-testid="im-confirm-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Enviar a ${confirm?.count ?? 0}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
